// web-commands.ts — the PER-APP half of the web shape: maps ielts_speaking_coach's Tauri
// data commands (normally served by ipc.rs/db.rs over native SQLite) to the shared browser
// WebStore. Plain CRUD is a few lines each; the derived aggregates (session summaries, band
// trend) are faithfully re-implemented in JS since they're simple averaging.
// The dormant native-only OCR command degrades to an empty result on web.

import type { CommandHandler, WebStore } from "./web-runtime";
import { browserSearch, browserFetch } from "./web-search-browser";

const SESSIONS = "sessions";
const TURNS = "turns";
const FEEDBACKS = "feedbacks";
const ACCENT_GOALS = "accent_goals";
const SHADOW = "shadow_attempts";
const BENCHMARKS = "benchmarks";
const PLANS = "practice_plans";

function uuid(): string {
  return typeof crypto !== "undefined" && "randomUUID" in crypto
    ? crypto.randomUUID()
    : `${Date.now()}-${Math.round(Math.random() * 1e9)}`;
}
function nowIso(): string {
  return new Date().toISOString();
}

/** Normalize a tag — lowercase, trim, collapse internal whitespace (mirrors db.rs::normalize). */
function normalizeTag(raw: string): string {
  return String(raw).trim().toLowerCase().split(/\s+/).filter(Boolean).join(" ");
}
function validExam(s: unknown): string {
  const v = normalizeTag(String(s ?? ""));
  return v === "ielts" || v === "toefl" ? v : "ielts";
}
function validMode(s: unknown): string {
  const v = normalizeTag(String(s ?? ""));
  return v === "mock" || v === "interview" || v === "listen_repeat" ? v : "mock";
}
function validPart(s: unknown): string | null {
  const v = normalizeTag(String(s ?? ""));
  return v === "p1" || v === "p2" || v === "p3" || v === "interview" || v === "repeat" ? v : null;
}
function clampBand(v: number): number {
  return Math.round(Math.min(9, Math.max(1, v)) * 2) / 2;
}

type Row = Record<string, unknown> & { id: string };

/** Attach the computed summary fields (counts + band range + repeat avg). */
function summarize(db: WebStore, session: Row): Row {
  const turns = db.all(TURNS).filter((t) => (t as { session_id?: string }).session_id === session.id);
  const answered = turns.filter((t) => (t as { answered_at?: string | null }).answered_at);
  const scores = turns
    .map((t) => (t as { score?: number | null }).score)
    .filter((v): v is number => typeof v === "number");
  const fbs = db.all(FEEDBACKS).filter((f) => (f as { session_id?: string }).session_id === session.id);
  const avg = (xs: number[]) => (xs.length ? xs.reduce((a, b) => a + b, 0) / xs.length : null);
  const lows = fbs.map((f) => Number((f as { band_low?: unknown }).band_low)).filter(Number.isFinite);
  const highs = fbs.map((f) => Number((f as { band_high?: unknown }).band_high)).filter(Number.isFinite);
  const round1 = (v: number | null) => (v == null ? null : Math.round(v * 10) / 10);
  return {
    ...session,
    turn_count: turns.length,
    answered_count: answered.length,
    band_low: round1(avg(lows)),
    band_high: round1(avg(highs)),
    avg_score: scores.length ? Math.round(avg(scores)!) : null,
  };
}

export function makeWebCommands(db: WebStore): Record<string, CommandHandler> {
  return {

    // ── WEB-SURFACE RETRIEVAL (Pattern 7, web half) ─────────────────────────────
    // On native, `web_search`/`web_fetch` are Rust commands browsing on the DEVICE. In a browser
    // there is no Rust, and without these the chat's `maybeWebContext` silently returns nothing —
    // the customer gets an ungrounded answer where the phone would have cited sources.
    // Here the BROWSER does it, on the customer's own IP: Tier 1 sources it may read directly
    // (no middleman), then a keyless reader for the open web. Oriora runs no search.
    web_search: async (args: Record<string, unknown>) =>
      await browserSearch(String(args.query ?? ""), typeof args.limit === "number" ? args.limit : 5),
    web_fetch: async (args: Record<string, unknown>) =>
      await browserFetch(String(args.url ?? ""), typeof args.maxChars === "number" ? args.maxChars : 6000),

    // ── Sessions ───────────────────────────────────────────────────────────────
    list_sessions: ({ limit }) => {
      const rows = db.all(SESSIONS).sort((a, b) =>
        String((b as { created_at?: string }).created_at ?? "").localeCompare(
          String((a as { created_at?: string }).created_at ?? ""),
        ),
      ) as Row[];
      const n = Number(limit);
      const sliced = Number.isFinite(n) && n > 0 ? rows.slice(0, n) : rows;
      return sliced.map((r) => summarize(db, r));
    },
    get_session: ({ id }) => {
      const row = db.get(SESSIONS, String(id)) as Row | null;
      return row ? summarize(db, row) : null;
    },
    create_session: ({ input }) => {
      const i = (input ?? {}) as Record<string, unknown>;
      const row: Row = {
        id: uuid(),
        exam: validExam(i.exam),
        mode: validMode(i.mode),
        topic: String(i.topic ?? "").trim(),
        created_at: nowIso(),
        updated_at: nowIso(),
      };
      db.insert(SESSIONS, row);
      return summarize(db, row);
    },
    delete_session: ({ id }) => {
      db.remove(SESSIONS, String(id));
      // ON DELETE CASCADE on native clears turns + feedbacks; mirror that here.
      for (const t of db.all(TURNS)) {
        if ((t as { session_id?: string }).session_id === String(id)) db.remove(TURNS, t.id);
      }
      for (const f of db.all(FEEDBACKS)) {
        if ((f as { session_id?: string }).session_id === String(id)) db.remove(FEEDBACKS, f.id);
      }
      return null;
    },
    set_topic: ({ id, topic }) => {
      db.update(SESSIONS, String(id), { topic: String(topic ?? "").trim(), updated_at: nowIso() });
      return null;
    },

    // ── Turns ──────────────────────────────────────────────────────────────────
    add_turns: ({ sessionId, part, prompts }) => {
      const p = validPart(part);
      if (!p) throw new Error("bad_part");
      const existing = db
        .all(TURNS)
        .filter((t) => (t as { session_id?: string }).session_id === String(sessionId) && (t as { part?: string }).part === p);
      let idx = existing.reduce((m, t) => Math.max(m, Number((t as { idx?: number }).idx ?? -1) + 1), 0);
      const out: Row[] = [];
      for (const q of (Array.isArray(prompts) ? prompts : [])) {
        const prompt = String((q as { prompt?: unknown })?.prompt ?? "").trim();
        if (!prompt) continue;
        const row: Row = {
          id: uuid(),
          session_id: String(sessionId),
          part: p,
          idx: idx++,
          prompt,
          hint: String((q as { hint?: unknown })?.hint ?? "").trim(),
          transcript: "",
          score: null,
          answered_at: null,
          created_at: nowIso(),
        };
        db.insert(TURNS, row);
        out.push(row);
      }
      db.update(SESSIONS, String(sessionId), { updated_at: nowIso() });
      return out;
    },
    list_turns: ({ sessionId }) => {
      const rows = db
        .all(TURNS)
        .filter((t) => (t as { session_id?: string }).session_id === String(sessionId)) as Row[];
      return rows.sort(
        (a, b) =>
          String(a.created_at ?? "").localeCompare(String(b.created_at ?? "")) ||
          Number(a.idx ?? 0) - Number(b.idx ?? 0),
      );
    },
    record_transcript: ({ turnId, transcript }) => {
      db.update(TURNS, String(turnId), {
        transcript: String(transcript ?? "").trim(),
        answered_at: nowIso(),
      });
      const t = db.get(TURNS, String(turnId)) as { session_id?: string } | null;
      if (t?.session_id) db.update(SESSIONS, t.session_id, { updated_at: nowIso() });
      return null;
    },
    record_repeat: ({ sessionId, prompt, transcript, score }) => {
      const existing = db
        .all(TURNS)
        .filter((t) => (t as { session_id?: string }).session_id === String(sessionId) && (t as { part?: string }).part === "repeat");
      const idx = existing.reduce((m, t) => Math.max(m, Number((t as { idx?: number }).idx ?? -1) + 1), 0);
      const row: Row = {
        id: uuid(),
        session_id: String(sessionId),
        part: "repeat",
        idx,
        prompt: String(prompt ?? "").trim(),
        hint: "",
        transcript: String(transcript ?? "").trim(),
        score: Math.round(Math.min(100, Math.max(0, Number(score) || 0))),
        answered_at: nowIso(),
        created_at: nowIso(),
      };
      db.insert(TURNS, row);
      db.update(SESSIONS, String(sessionId), { updated_at: nowIso() });
      return row;
    },

    // ── Feedback ───────────────────────────────────────────────────────────────
    add_feedback: ({ sessionId, part, feedbackJson, bandLow, bandHigh }) => {
      const p = validPart(part);
      if (!p) throw new Error("bad_part");
      const low = clampBand(Number(bandLow) || 1);
      const high = clampBand(Math.max(Number(bandHigh) || 1, low));
      const row: Row = {
        id: uuid(),
        session_id: String(sessionId),
        part: p,
        feedback_json: String(feedbackJson ?? ""),
        band_low: low,
        band_high: high,
        created_at: nowIso(),
      };
      db.insert(FEEDBACKS, row);
      db.update(SESSIONS, String(sessionId), { updated_at: nowIso() });
      return row;
    },
    list_feedbacks: ({ sessionId }) => {
      const rows = db
        .all(FEEDBACKS)
        .filter((f) => (f as { session_id?: string }).session_id === String(sessionId)) as Row[];
      return rows.sort((a, b) => String(a.created_at ?? "").localeCompare(String(b.created_at ?? "")));
    },
    // Mirrors db.rs::list_all_feedbacks — every feedback, oldest first.
    list_all_feedbacks: () =>
      (db.all(FEEDBACKS) as Row[])
        .slice()
        .sort((a, b) => String(a.created_at ?? "").localeCompare(String(b.created_at ?? ""))),

    // ── Derived aggregate — band trend (mirrors db.rs::band_trend) ─────────────
    band_trend: () => {
      const bySession = new Map<string, number[]>();
      for (const f of db.all(FEEDBACKS)) {
        const sid = String((f as { session_id?: string }).session_id ?? "");
        const low = Number((f as { band_low?: unknown }).band_low);
        const high = Number((f as { band_high?: unknown }).band_high);
        if (!sid || !Number.isFinite(low) || !Number.isFinite(high)) continue;
        const arr = bySession.get(sid) ?? [];
        arr.push((low + high) / 2);
        bySession.set(sid, arr);
      }
      const out: { session_id: string; date: string; band_mid: number }[] = [];
      for (const [sid, mids] of bySession) {
        const s = db.get(SESSIONS, sid) as { created_at?: string } | null;
        if (!s) continue;
        out.push({
          session_id: sid,
          date: String(s.created_at ?? ""),
          band_mid: Math.round((mids.reduce((a, b) => a + b, 0) / mids.length) * 10) / 10,
        });
      }
      return out.sort((a, b) => a.date.localeCompare(b.date));
    },

    // ── Accent Lab (2026-08-27) — parity with db.rs; every field written explicitly ──
    create_accent_goal: ({ name, lang, targetDate }) => {
      const row: Row = { id: uuid(), name: String(name ?? "").trim(), lang: String(lang ?? "en"), target_date: String(targetDate ?? ""), created_at: nowIso() };
      db.insert(ACCENT_GOALS, row);
      return row;
    },
    list_accent_goals: () => db.all(ACCENT_GOALS).sort((a, b) => String((a as Row).created_at).localeCompare(String((b as Row).created_at))),
    delete_accent_goal: ({ id }) => { db.remove(ACCENT_GOALS, String(id)); return null; },
    save_shadow_attempt: ({ lang, drillKey, sentence, heard, localScore, aiJson, audioRef }) => {
      const row: Row = {
        id: uuid(),
        lang: String(lang ?? "en"),
        drill_key: String(drillKey ?? ""),
        sentence: String(sentence ?? ""),
        heard: String(heard ?? ""),
        local_score: localScore == null ? null : Number(localScore),
        ai_json: String(aiJson ?? ""),
        audio_ref: String(audioRef ?? ""),
        created_at: nowIso(),
      };
      db.insert(SHADOW, row);
      return row;
    },
    list_shadow_days: () => {
      const days = new Set<string>();
      for (const r of db.all(SHADOW)) days.add(String((r as Row).created_at ?? "").slice(0, 10));
      return [...days].sort().reverse().slice(0, 400);
    },
    list_shadow_attempts: ({ limit }) =>
      db.all(SHADOW)
        .sort((a, b) => String((b as Row).created_at).localeCompare(String((a as Row).created_at)))
        .slice(0, Number(limit ?? 50)),
    save_benchmark: ({ lang, passage, audioRef }) => {
      const row: Row = { id: uuid(), lang: String(lang ?? "en"), passage: String(passage ?? ""), audio_ref: String(audioRef ?? ""), recorded_on: nowIso() };
      db.insert(BENCHMARKS, row);
      return row;
    },
    list_benchmarks: () => db.all(BENCHMARKS).sort((a, b) => String((a as Row).recorded_on).localeCompare(String((b as Row).recorded_on))),

    save_practice_plan: ({ lang, name, planJson }) => {
      const id = String(lang ?? "en");
      const existing = db.get(PLANS, id);
      if (existing) db.update(PLANS, id, { name: String(name ?? ""), plan_json: String(planJson ?? "") });
      else db.insert(PLANS, { id, lang: id, name: String(name ?? ""), plan_json: String(planJson ?? ""), created_at: nowIso() });
      return null;
    },
    get_practice_plan: ({ lang }) => {
      const row = db.get(PLANS, String(lang ?? "en"));
      return row ?? null;
    },
    delete_practice_plan: ({ lang }) => { db.remove(PLANS, String(lang ?? "en")); return null; },

    // ── Native-only (dormant Apple Vision OCR). Web returns empty → callers no-op. ─
    ocr_image: () => "",
  };
}
